import { InputHTMLAttributes } from 'react';
import { useFormContext } from 'react-hook-form';
import clsx from 'clsx';
import { debounce } from 'lodash';
import { formatDateToISO } from '@app/utils/utils';

interface InputFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  containerClass?: string;
  isOptional?: boolean;
  sizeStyle?: 'sm' | 'md' | 'lg';
  preventPastDates?: boolean;
}

const getStyle = (size: string) => {
  switch (size) {
    case 'sm': {
      return 'text-sm py-2 px-3';
    }
    case 'md': {
      return 'text-normal py-3 px-4';
    }
    case 'lg': {
      return 'text-lg py-4 px-5';
    }
  }
};

export const DateField = ({
  name,
  label,
  containerClass,
  isOptional,
  sizeStyle = 'md',
  ...rest
}: InputFieldProps) => {
  const {
    register,
    // @ts-expect-error this are need to update the input state on errors/etc
    // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
    formState: { errors, isDirty },
  } = useFormContext();

  return (
    <div className={`w-full relative ${containerClass ?? ''}`}>
      {label && (
        <label htmlFor={name} className="font-medium text-sm inline-block mb-1">
          {label} {isOptional && <span>(optional)</span>}
        </label>
      )}
      <input
        {...rest}
        {...(rest.placeholder
          ? isOptional
          ? { placeholder: rest.placeholder + ' (optional)' }
          : { placeholder: rest.placeholder }
          : {})}
        type="date"
        {...register(name ?? '')}
        min={rest.preventPastDates ? formatDateToISO(new Date()) : undefined}
        onKeyDown={(e) => {
          // Prevent typing by blocking keydown events
          e.preventDefault();
        }}
        onClick={(e) => {
          // Open datepicker on click
          e.currentTarget.showPicker();
        }}
        onChange={debounce((e) => {
          register(name ?? '').onChange(e);
        }, 500)}
        className={clsx(
          `bg-base-seasalt rounded py-3 px-4 w-full outline-brand-baseBlue outline-1 ${rest.className ?? ''} ${getStyle(sizeStyle)}`,
          { 'border border-system-error': name && errors[name] }
        )}
      />

      {name && errors[name] && (
        <p role="alert" className="text-system-error text-sm text-left pt-1">
          {typeof errors?.[name]?.message === 'string'
            ? errors[name]?.message
            : ''}
        </p>
      )}
    </div>
  );
};
